<script setup>
import { defineProps, defineEmits } from "vue";

const props = defineProps({
  activePage: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  totalVisiblePages: { // Общее количество отображаемых страниц
    type: Number,
    default: 5,
  },
});

const emit = defineEmits(["pageChange"]);

// Функция для генерации страниц с "..."
const generatePages = () => {
  const pages = [];
  const { activePage, totalPages, totalVisiblePages } = props;

  if (totalPages <= totalVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Количество "центральных" страниц между первой и последней
  const centralCount = totalVisiblePages - 2; // первая и последняя всегда есть
  let start = activePage - Math.floor(centralCount / 2);
  let end = activePage + Math.floor((centralCount - 1) / 2);

  // Корректируем если вышли за границы
  if (start < 2) {
    end += 2 - start;
    start = 2;
  }
  if (end > totalPages - 1) {
    start -= end - (totalPages - 1);
    end = totalPages - 1;
  }
  start = Math.max(start, 2);
  end = Math.min(end, totalPages - 1);

  pages.push(1);

  if (start > 2) {
    pages.push("...");
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < totalPages - 1) {
    pages.push("...");
  }

  pages.push(totalPages);

  return pages;
};

const setPage = (page) => {
  if (page === "...") return; // Игнорируем клики на "..."
  if (page >= 1 && page <= props.totalPages) {
    emit("pageChange", page);
  }
};
</script>

<template>
  <div class="pagination">
    <!-- Кнопка "Назад" -->
    <button
      class="pagination__control pagination__control--back"
      :disabled="activePage === 1"
      @click="setPage(activePage - 1)"
    >
      <i class="icon-check-mark" />
    </button>

    <!-- Ссылки на страницы -->
    <button
      v-for="page in generatePages()"
      :key="page"
      :class="{ 'pagination__page--active': page === activePage }"
      class="pagination__page"
      @click="setPage(page)"
    >
      {{ page }}
    </button>

    <!-- Кнопка "Вперед" -->
    <button
      class="pagination__control"
      :disabled="activePage === totalPages"
      @click="setPage(activePage + 1)"
    >
      <i class="icon-check-mark" />
    </button>
    <div class="pagination__total">
      Всего тегов: <b>{{ totalPages * 10 }}</b>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/styles/vars.scss' as *;

.pagination {
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  margin: 16px 0;
  font-size: 14px;
  font-weight: 500;

 &__control {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    color: #333;
    width: 32px;
    height: 32px;
    margin: 0 4px;
    line-height: normal;
    transition: color 0.3s ease;
    
    & i {
      display: inline-block;
    }

    &--back i {
      transform: rotate(180deg);
    }

    &:hover {
      color: #007bff;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__page {
    color: #333;
    width: 32px;
    height: 32px;
    margin: 0 7px;
    line-height: normal;
    transition: color 0.3s ease;
    &:not(.pagination__page--active) {
      cursor: pointer;
    } 

    &:hover:not(.pagination__page--active) {
      background: var(--text-01-inverse);
      border-radius: 50%;
      color: #007bff;
    }

    &--active {
      background: var(--text-01-inverse);
      border-radius: 50%;
      font-weight: bold;
      cursor: default;
    }
  }
  &__total {
    position: absolute;
    right: 15px;
    font-weight: 400;
    color: #A7A7A7;
  }
}
</style>
